import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProvince } from '../hooks/useProvince';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { searchSnapshots, type SnapshotSearchResponse, type SnapshotSearchParams, type ProvinceSnapshotDto } from '../api/intel';
import { buildTargetSearchParams, type TargetFilterState, type SortKey } from '../utils/targetSearchParams';
import { DesktopTargetFinderShell } from '../components/targetFinder/DesktopTargetFinderShell';
import { MobileTargetFinderShell } from '../components/targetFinder/MobileTargetFinderShell';
import { COLORS } from '../constants/theme';

/** Mobile's page size (20) vs. desktop's mirrored 50 — see Component 5's results-count rationale. */
export const MOBILE_PAGE_SIZE = 20;

const DEFAULT_MOBILE_FILTERS: TargetFilterState = {
  raceEnabled: false, raceSelected: [],
  rankEnabled: false, rankSelected: [],
  nwEnabled: true, nwMin: '80', nwMax: '120',
  landEnabled: false, landMin: '80', landMax: '120',
  nwpaEnabled: false, nwpaMin: '80', nwpaMax: '120',
  kdNwEnabled: false, kdNwMin: '80', kdNwMax: '120',
  kdLandEnabled: false, kdLandMin: '80', kdLandMax: '120',
};

/**
 * SF-171: named, fully-typed props shared by DesktopTargetFinderShell/MobileTargetFinderShell —
 * same `ExploreShellProps`/`TargetFinderShellProps` precedent as SF-139/SF-132. Desktop consumes
 * only `worldId`/`provinceId`/`results`/`onResults`/`expandedId`/`onExpandedId` (passed straight
 * through to the unmodified `TargetFinderTab`, which owns its own filter/sort/page state
 * internally). Mobile consumes the rest — its own page-owned filter/sort/page state, kept
 * independent from desktop's so neither can desync from the other.
 */
export interface TargetFinderShellProps {
  worldId: string;
  provinceId: string;
  results: SnapshotSearchResponse | null;
  onResults: (results: SnapshotSearchResponse | null) => void;
  expandedId: string | null;
  onExpandedId: (id: string | null) => void;
  onSearch: (params: SnapshotSearchParams) => Promise<void>;
  loading: boolean;
  error: string | null;
  mobileFilters: TargetFilterState;
  onMobileFiltersChange: (filters: TargetFilterState) => void;
  sortBy: SortKey;
  sortDir: 'asc' | 'desc';
  onSort: (sortBy: SortKey, sortDir: 'asc' | 'desc') => void;
  page: number;
  onPageChange: (page: number) => void;
  selectedTarget: ProvinceSnapshotDto | null;
  onSelectTarget: (target: ProvinceSnapshotDto | null) => void;
  currentTick: number;
}

export interface TargetFinderNavState {
  /** Set by Kingdom/World Viewer roster rows to jump straight to this province's detail view,
   *  skipping a search (Component: roster-to-target-detail deep link). */
  presetTarget?: ProvinceSnapshotDto;
  presetCurrentTick?: number;
}

export function TargetFinder() {
  const { worldId, provinceId } = useAuth();
  const { province } = useProvince();
  const platform = useAppShellPlatform();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const ownNetworth = province?.metadata.networth ?? null;
  const presetTarget = (location.state as TargetFinderNavState | null)?.presetTarget ?? null;
  const presetCurrentTick = (location.state as TargetFinderNavState | null)?.presetCurrentTick ?? 0;

  // Committed mobile search state, page-owned (see Application Interface / Phase 3 Decision 5) —
  // mechanically flip-persistent since this page component never unmounts on a breakpoint flip.
  const [results, setResults] = useState<SnapshotSearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [mobileFilters, setMobileFilters] = useState<TargetFilterState>(DEFAULT_MOBILE_FILTERS);
  const [sortBy, setSortBy] = useState<SortKey>('nw');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);

  // Discards a stale response if a rapid second search fires before the first resolves.
  const requestTokenRef = useRef(0);

  // A province row clicked in the Kingdom/World Viewer roster arrives here with a ready-made
  // snapshot in nav state — seed it as a synthetic single-result "search" (mobile selects it via
  // the same `?target=` mechanism a real search result would; desktop auto-expands its row) so
  // both shells land straight on the detail view instead of an empty search form.
  const presetHandledRef = useRef(false);
  useEffect(() => {
    if (presetHandledRef.current) return;
    presetHandledRef.current = true;
    if (presetTarget) {
      setResults({
        results: [presetTarget],
        totalCount: 1,
        page: 0,
        size: 1,
        player: null,
        currentTick: presetCurrentTick,
      });
      setExpandedId(presetTarget.provinceId);
      setSearchParams({ target: presetTarget.provinceId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = useCallback(
    async (params: SnapshotSearchParams) => {
      if (!worldId) return;
      const token = ++requestTokenRef.current;
      setLoading(true);
      setError(null);
      try {
        const response = await searchSnapshots(worldId, params);
        if (token !== requestTokenRef.current) return; // stale — a newer search has since started
        setResults(response);
      } catch (err) {
        if (token !== requestTokenRef.current) return;
        setError(err instanceof Error ? err.message : 'Search failed');
      } finally {
        if (token === requestTokenRef.current) setLoading(false);
      }
    },
    [worldId]
  );

  // Mobile auto-search on mount: fires once, defaulting to own networth ±20% (seeded from
  // `province.networth`, already fetched pre-search), if no search has resolved yet. Never
  // reads/writes the `?target=` param and the `selectedTarget` derivation below never triggers a
  // search — see Key Implementation Note 9, this can't race the SF-148 deep-link class of bug.
  const autoSearchFiredRef = useRef(false);
  useEffect(() => {
    if (presetTarget) return;
    if (platform === 'mobile' && results === null && !autoSearchFiredRef.current) {
      autoSearchFiredRef.current = true;
      void onSearch(buildTargetSearchParams(DEFAULT_MOBILE_FILTERS, sortBy, sortDir, 0, MOBILE_PAGE_SIZE, null, ownNetworth));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, results, onSearch, presetTarget]);

  const onMobileFiltersChange = useCallback(
    (filters: TargetFilterState) => {
      setMobileFilters(filters);
      setPage(0);
      void onSearch(buildTargetSearchParams(filters, sortBy, sortDir, 0, MOBILE_PAGE_SIZE, results?.player ?? null, ownNetworth));
    },
    [onSearch, sortBy, sortDir, results, ownNetworth]
  );

  const onSort = useCallback(
    (newSortBy: SortKey, newSortDir: 'asc' | 'desc') => {
      setSortBy(newSortBy);
      setSortDir(newSortDir);
      setPage(0);
      void onSearch(buildTargetSearchParams(mobileFilters, newSortBy, newSortDir, 0, MOBILE_PAGE_SIZE, results?.player ?? null, ownNetworth));
    },
    [onSearch, mobileFilters, results, ownNetworth]
  );

  const onPageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      void onSearch(buildTargetSearchParams(mobileFilters, sortBy, sortDir, newPage, MOBILE_PAGE_SIZE, results?.player ?? null, ownNetworth));
    },
    [onSearch, mobileFilters, sortBy, sortDir, results, ownNetworth]
  );

  // `?target=<provinceId>` — derived selection, not independent state (Phase 3 Decision 6).
  const targetParam = searchParams.get('target');
  const selectedTarget = useMemo(
    () => results?.results.find((r) => r.provinceId === targetParam) ?? null,
    [results, targetParam]
  );

  const onSelectTarget = useCallback(
    (target: ProvinceSnapshotDto | null) => {
      if (target) {
        setSearchParams({ target: target.provinceId });
      } else {
        setSearchParams({});
      }
    },
    [setSearchParams]
  );

  if (!worldId || !provinceId) {
    return <div style={{ color: COLORS.text.muted, padding: '24px' }}>Loading...</div>;
  }

  const shellProps: TargetFinderShellProps = {
    worldId,
    provinceId,
    results,
    onResults: setResults,
    expandedId,
    onExpandedId: setExpandedId,
    onSearch,
    loading,
    error,
    mobileFilters,
    onMobileFiltersChange,
    sortBy,
    sortDir,
    onSort,
    page,
    onPageChange,
    selectedTarget,
    onSelectTarget,
    currentTick: results?.currentTick ?? 0,
  };

  return platform === 'mobile'
    ? <MobileTargetFinderShell {...shellProps} />
    : <DesktopTargetFinderShell {...shellProps} />;
}
